import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import style from "./index.module.scss";
import WrapperContent from "../../../components/shared/WrapperContent";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import CloseIcon from "../../../components/shared/icons/CloseIcon";
import InputCustom from "../../../components/shared/forms/input-custom";
import UserIcon from "../../../components/shared/icons/UserIcon";
import PhoneIcon from "../../../components/shared/icons/PhoneIcon";
import EmailIcon from "../../../components/shared/icons/EmailIcon";
import AvatarCameraIcon from "../../../components/shared/icons/AvatarCameraIcon";
import { Driver, User, File } from "../../../services";
import { requestErrorDisplay } from "../../../utils";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import InputMask from 'react-input-mask';
import Loading from "../../../components/shared/Loading/Loading";
import cn from "classnames";
import InviteLinkModal from "../../../components/drivers/InviteLinkModal/InviteLinkModal";

const DriversInvitePage = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const driversUrl = "/drivers";
  const backUrl = searchParams.get("redirectUrl") ? searchParams.get("redirectUrl") : driversUrl;
  const [isLoading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [patronymic, setPatronymic] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [carIdPhotoOne, setCarIdPhotoOne] = useState(null);
  const [carIdPhotoTwo, setCarIdPhotoTwo] = useState(null);
  const [userIdPhotoOne, setUserIdPhotoOne] = useState(null);
  const [userIdPhotoTwo, setUserIdPhotoTwo] = useState(null);
  const [userPhoto, setUserPhoto] = useState(null);
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [showPhoneError, setShowPhoneError] = useState(false);
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [inviteUrl, setInviteUrl] = useState("");
  const [createdDriverName, setCreatedDriverName] = useState("");
  const [expireLink, setExpireLink] = useState("");

  const handlePhoneChange = (value) => {
    const numbersOnly = value.replace(/\D/g, "");
    setPhoneNumber(numbersOnly);
  };

  const validateFormatData = () => {
    if (!name.trim()) {
      toast.error(t("driversCreatePage.validation.invalidName"));
      return false;
    }

    if (!phoneNumber || phoneNumber.length < 10) {
      toast.error(t("driversCreatePage.validation.invalidPhone"));
      return false;
    }

    if (email && email.trim() && !isValidEmail(email)) {
      toast.error(t("driversCreatePage.validation.invalidEmailFormat"));
      return false;
    }

    if (!carIdPhotoOne || !carIdPhotoTwo) {
      toast.error(t("driversCreatePage.validation.invalidDriverLicensePhotos"));
      return false;
    }

    if (!userIdPhotoOne || !userIdPhotoTwo) {
      toast.error(t("driversCreatePage.validation.invalidIdPhotos"));
      return false;
    }

    if (!userPhoto) {
      toast.error(t("driversCreatePage.validation.invalidSelfiePhoto"));
      return false;
    }

    return true;
  };

  const checkPhoneExists = async (phoneToCheck) => {
    if (!phoneToCheck) return { exists: false, error: null };

    if (phoneToCheck.length < 10) return { exists: false, error: null };

    setIsCheckingPhone(true);
    setPhoneError("");

    try {
      const response = await User.check("phoneNumber", phoneToCheck);

      const exists = response.data?.isExists || false;

      if (exists) {
        const errorMessage = response.data?.errorText || t("driversCreatePage.toast.phoneExists");
        setPhoneError(errorMessage);
        return { exists: true, error: errorMessage };
      }

      return { exists: false, error: null };
    } catch (error) {
      console.error("Error checking phone:", error);
      const errorMessage = t("driversCreatePage.toast.phoneCheckError");
      setPhoneError(errorMessage);
      return { exists: false, error: errorMessage };
    } finally {
      setIsCheckingPhone(false);
    }
  };

  const uploadFile = (data) => {
    const Formdata = new FormData();
    for (const dataKey in data) {
      Formdata.append(dataKey, data[dataKey]);
    }
    return File.uploadDocumentDriver(Formdata);
  };

  const onSubmit = async () => {
    setShowPhoneError(true);

    if (!validateFormatData()) {
      return false;
    }

    const phoneCheckResult = await checkPhoneExists(phoneNumber);
    if (phoneCheckResult.exists) {
      toast.error(phoneCheckResult.error);
      return false;
    }

    if (phoneCheckResult.error) {
      toast.error(phoneCheckResult.error);
      return false;
    }

    setLoading(true);
    const data = {
      name: name.trim(),
      surname: surname.trim(),
      patronymic: patronymic.trim(),
      email: email.trim(),
      phoneNumber: phoneNumber, // Номер уже содержит только цифры
    };

    Driver.createCnDriver(data)
      .then((res) => res.data)
      .then(async (data) => {
        const driverId = data.driver.id;

        try {
          await uploadFile({
            driverId,
            side: 0,
            file: carIdPhotoOne,
            type: 6,
          });
          await uploadFile({
            driverId,
            side: 1,
            file: carIdPhotoTwo,
            type: 6,
          });
          await uploadFile({
            driverId,
            side: 0,
            file: userIdPhotoOne,
            type: 3,
          });
          await uploadFile({
            driverId,
            side: 1,
            file: userIdPhotoTwo,
            type: 3,
          });
          await uploadFile({
            driverId,
            side: 0,
            file: userPhoto,
            type: 8,
          });
        } catch (uploadError) {
          console.error("Error uploading files:", uploadError);
          toast.error(t("driversCreatePage.toast.fileUploadError"));
          return;
        }

        toast.success(t("driversCreatePage.toast.createSuccess"));

        if (data.url) {
          setInviteUrl(data.url);
          setCreatedDriverName(name.trim());
          setExpireLink(data?.expiry);
          setShowModal(true);
        }
      })
      .catch((error) => {
        requestErrorDisplay(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const isValidEmail = (email) => {
    if (!email || email.trim() === "") return true;

    const trimmedEmail = email.trim();

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(trimmedEmail);
  };

  const handleEmailChange = (value, setter, setError = null) => {
    setter(value);

    const trimmedValue = value.trim();

    if (trimmedValue !== "") {
      const isValid = isValidEmail(value);

      if (setError) {
        setError(!isValid);
      }

      return isValid;
    } else {
      if (setError) {
        setError(false);
      }
      return true;
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    navigate(backUrl);
  };

  return (
    <WrapperContent>
      <div className={style.wrapper}>
        <h1 className={style.title}>{t("driversCreatePage.inviteDriver")}</h1>
        <div className={style.content}>
          <div className={style.field}>
            <span className={style.field_label}>{t("driversCreatePage.name.label")}</span>
            <InputCustom
              placeholder={t("driversCreatePage.name.placeholder")}
              defaultValue={name}
              onChangeValue={setName}
              prefixStart={<UserIcon />}
            />
          </div>
          <div className={style.field}>
            <span className={style.field_label}>
              {t("driversCreatePage.lastName.label")}
              <span className={style.optional}>{t("driversCreatePage.email.optional")}</span>
            </span>
            <InputCustom
              placeholder={t("driversCreatePage.lastName.placeholder")}
              defaultValue={surname}
              onChangeValue={setSurname}
              prefixStart={<UserIcon />}
            />
          </div>
          <div className={style.field}>
            <span className={style.field_label}>
              {t("driversCreatePage.patronymic.label")}
              <span className={style.optional}>{t("driversCreatePage.email.optional")}</span>
            </span>
            <InputCustom
              placeholder={t("driversCreatePage.patronymic.placeholder")}
              defaultValue={patronymic}
              onChangeValue={setPatronymic}
              prefixStart={<UserIcon />}
            />
          </div>
          <div className={style.field}>
            <span className={style.field_label}>{t("driversCreatePage.phone.label")}</span>
            {user.isCnUser && (<InputCustom
              placeholder="13800138000"
              defaultValue={phoneNumber}
              onChangeValue={handlePhoneChange}
              prefixStart={<PhoneIcon />}
              inputType="number"
              className={showPhoneError && phoneError ? style.input_error : ""}
            />)}
            {!user.isCnUser && (<InputMask
              value={phoneNumber}
              maskChar={null}
              mask="+7 (999) 999-99-99"
              onChange={(event) => handlePhoneChange}
              disabled={false}
            >
              {(inputProps) => (
                <InputCustom
                  {...inputProps}
                  placeholder="+7-777-777-77-77"
                  prefixStart={<PhoneIcon />}
                  defaultValue={phoneNumber}
                  onChangeValue={handlePhoneChange}
                  maxLength={18}
                  className={showPhoneError && phoneError ? style.input_error : ''}
                />
              )}
            </InputMask>)}
            {showPhoneError && phoneError && <p className={style.error_message}>{phoneError}</p>}
          </div>
          <div className={style.field}>
            <span className={style.field_label}>
              {t("driversCreatePage.email.label")}
              <span className={style.optional}>{t("driversCreatePage.email.optional")}</span>
            </span>
            <InputCustom
              defaultValue={email}
              onChangeValue={(value) => handleEmailChange(value, setEmail, setEmailError)}
              placeholder={t("driversCreatePage.email.placeholder")}
              prefixStart={<EmailIcon />}
              className={emailError ? style.error : ""}
            />
            {emailError && (
              <p className={style.error_message}>{t("driversCreatePage.validation.invalidEmailFormat")}</p>
            )}
          </div>

          <div className={style.field}>
            <span className={style.field_label}>{t("driversCreatePage.driverLicensePhotos.label")}</span>
            <div className={style.field_col_2}>
              <label className={style.field_upload}>
                <AvatarCameraIcon fill={"#C3C5CA"} />
                <span>{carIdPhotoOne?.name || t("driversCreatePage.driverLicensePhotos.front")}</span>
                <input
                  type="file"
                  onChange={(event) => setCarIdPhotoOne(event.target.files[0])}
                />
              </label>
              <label className={style.field_upload}>
                <AvatarCameraIcon fill={"#C3C5CA"} />
                <span>{carIdPhotoTwo?.name || t("driversCreatePage.driverLicensePhotos.back")}</span>
                <input
                  type="file"
                  onChange={(event) => setCarIdPhotoTwo(event.target.files[0])}
                />
              </label>
            </div>
          </div>
          <div className={style.field}>
            <span className={style.field_label}>{t("driversCreatePage.idPhotos.label")}</span>
            <div className={style.field_col_2}>
              <label className={style.field_upload}>
                <AvatarCameraIcon fill={"#C3C5CA"} />
                <span>{userIdPhotoOne?.name || t("driversCreatePage.idPhotos.front")}</span>
                <input
                  type="file"
                  onChange={(event) => setUserIdPhotoOne(event.target.files[0])}
                />
              </label>
              <label className={style.field_upload}>
                <AvatarCameraIcon fill={"#C3C5CA"} />
                <span>{userIdPhotoTwo?.name || t("driversCreatePage.idPhotos.back")}</span>
                <input
                  type="file"
                  onChange={(event) => setUserIdPhotoTwo(event.target.files[0])}
                />
              </label>
            </div>
          </div>
          <div className={style.field}>
            <span className={style.field_label}>{t("driversCreatePage.selfiePhoto.label")}</span>
            <div className={style.field_col_2}>
              <label className={style.field_upload}>
                <AvatarCameraIcon fill={"#C3C5CA"} />
                <span>{userPhoto?.name || t("driversCreatePage.selfiePhoto.placeholder")}</span>
                <input
                  type="file"
                  onChange={(event) => setUserPhoto(event.target.files[0])}
                />
              </label>
            </div>
          </div>

          <button
            className={cn(style.button_upload, {
              [style.button_upload_loading]: isLoading,
            })}
            onClick={() => onSubmit()}
            disabled={isLoading || isCheckingPhone}
          >
            {!isLoading && <span>{t("driversCreatePage.buttons.confirm")}</span>}
            {isLoading && <Loading inBlock marginBottom={0} />}
          </button>
        </div>
        <Link to={backUrl} className={style.buttonCancel}>
          {t("driversCreatePage.buttons.cancel")}
        </Link>
        <Link to={backUrl} className={style.buttonClose}>
          <CloseIcon />
        </Link>
      </div>

      <InviteLinkModal
        isOpen={showModal}
        onClose={handleModalClose}
        inviteUrl={inviteUrl}
        driverName={createdDriverName}
        expiry={expireLink}
      />
    </WrapperContent>
  );
};

export default DriversInvitePage;
